import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import {
  activitySchema,
  pageSchema,
  taskCardSchema,
  taskDetailSchema,
  type Activity,
  type CreateTaskRequest,
  type TaskCard,
  type TaskDetail,
  type TaskStatus,
  type UpdateTaskRequest,
} from '@witty-board/shared'
import { apiRequest, apiRequestVoid } from './client.js'
import { buildQueryString } from '../lib/query-string.js'
import { applyOptimisticMove } from '../lib/board.js'

/** `projectId` para el tablero de un proyecto, `assigneeId` para "Mis tareas"; se pueden combinar. */
export interface TaskListFilters {
  projectId?: string
  assigneeId?: string
  /** Papelera (CLAUDE.md §6, fase 5). */
  onlyDeleted?: boolean
  /** Ventana de la columna Finalizada (fase 7): ver `lib/done-window.ts`. */
  doneSince?: string
}

export function tasksQueryKey(filters: TaskListFilters) {
  return ['tasks', filters] as const
}

/** Suficiente para no paginar de verdad con "3 usuarios durante mucho tiempo" (CLAUDE.md). */
const PAGE_LIMIT = 100

/**
 * El tablero necesita la columna completa para calcular índices de arrastre
 * correctos, así que agota el cursor en vez de pintar una página parcial (ver
 * docs/DECISIONS.md, fase 3).
 */
export async function fetchAllTasks(filters: TaskListFilters): Promise<TaskCard[]> {
  const items: TaskCard[] = []
  let cursor: string | undefined

  do {
    const query = buildQueryString({ ...filters, cursor, limit: PAGE_LIMIT })
    const page = await apiRequest(`/tasks${query}`, pageSchema(taskCardSchema))
    items.push(...page.items)
    cursor = page.nextCursor ?? undefined
  } while (cursor)

  return items
}

export function useTasksQuery(filters: TaskListFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: tasksQueryKey(filters),
    queryFn: () => fetchAllTasks(filters),
    enabled: options.enabled ?? true,
  })
}

export interface MoveTaskVars {
  id: string
  status: TaskStatus
  /** Índice destino dentro de la columna, no la posición real (services/position.ts la calcula). */
  position: number
}

/** Mueve una tarjeta con actualización optimista y reversión si el API falla. */
export function useMoveTaskMutation(filters: TaskListFilters) {
  const queryClient = useQueryClient()
  const key = tasksQueryKey(filters)

  return useMutation({
    mutationFn: (vars: MoveTaskVars) =>
      apiRequest(`/tasks/${vars.id}/move`, taskCardSchema, {
        method: 'POST',
        body: { status: vars.status, position: vars.position },
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<TaskCard[]>(key)
      if (previous) {
        queryClient.setQueryData<TaskCard[]>(
          key,
          applyOptimisticMove(previous, vars.id, vars.status, vars.position),
        )
      }
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export function taskDetailQueryKey(id: string) {
  return ['task', id] as const
}

/** `GET /tasks/{id}`: la tarjeta más hijas, comentarios e historial (CLAUDE.md §5). */
export function useTaskDetailQuery(id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskDetailQueryKey(id),
    queryFn: () => apiRequest(`/tasks/${id}`, taskDetailSchema),
    enabled: options.enabled ?? true,
  })
}

/** Edición en línea de cualquier campo del panel de detalle: cada campo llama a esto por su cuenta. */
export function useUpdateTaskMutation(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateTaskRequest) =>
      apiRequest(`/tasks/${id}`, taskCardSchema, { method: 'PATCH', body: request }),
    onSuccess: (updated) => {
      queryClient.setQueryData<TaskDetail>(taskDetailQueryKey(id), (current) =>
        current ? { ...current, ...updated } : current,
      )
      // El PATCH deja una fila nueva en `activity`: sin refetch, el hilo del
      // panel no la vería hasta volver a abrirlo.
      void queryClient.invalidateQueries({ queryKey: taskDetailQueryKey(id) })
      void queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false })
    },
  })
}

export interface DeleteTaskVars {
  id: string
  cascade: boolean
}

/** Borrado suave; `cascade` es obligatorio cuando la tarea tiene hijas (CLAUDE.md §4). */
export function useDeleteTaskMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, cascade }: DeleteTaskVars) =>
      apiRequestVoid(`/tasks/${id}${buildQueryString({ cascade })}`, { method: 'DELETE' }),
    onSuccess: (_result, vars) => {
      queryClient.removeQueries({ queryKey: taskDetailQueryKey(vars.id) })
      void queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false })
      // Si era una subtarea, la lista de hijas de su padre también cambia.
      void queryClient.invalidateQueries({ queryKey: ['task'], exact: false })
    },
  })
}

function optimisticTaskCard(
  tempId: string,
  request: CreateTaskRequest,
  authorId: string,
): TaskCard {
  const now = new Date().toISOString()
  return {
    id: tempId,
    projectId: request.projectId,
    parentId: request.parentId ?? null,
    title: request.title,
    description: request.description ?? null,
    status: request.status,
    size: request.size ?? null,
    priority: request.priority,
    assigneeId: request.assigneeId ?? null,
    dueDate: request.dueDate ?? null,
    position: request.position ?? Number.MAX_SAFE_INTEGER,
    createdBy: authorId,
    completedAt: request.status === 'done' ? now : null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    labelIds: request.labelIds ?? [],
    commentCount: 0,
    subtaskCount: 0,
    subtaskDoneCount: 0,
  }
}

/** Alta rápida desde la cabecera de columna: solo título (CLAUDE.md, fase 3). */
export function useCreateTaskMutation(filters: TaskListFilters, authorId: string) {
  const queryClient = useQueryClient()
  const key = tasksQueryKey(filters)

  return useMutation({
    mutationFn: (request: CreateTaskRequest) =>
      apiRequest('/tasks', taskCardSchema, { method: 'POST', body: request }),
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<TaskCard[]>(key)
      const tempId = `optimistic-${crypto.randomUUID()}`
      const optimisticTask = optimisticTaskCard(tempId, request, authorId)
      queryClient.setQueryData<TaskCard[]>(key, [...(previous ?? []), optimisticTask])
      return { previous, tempId }
    },
    onError: (_error, _request, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSuccess: (created, _request, context) => {
      queryClient.setQueryData<TaskCard[]>(key, (current) =>
        (current ?? []).map((task) => (task.id === context.tempId ? created : task)),
      )
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
      // Si `request.parentId` está abierto en el panel de detalle, su lista
      // de hijas necesita refrescarse también (alta de subtareas, fase 4).
      void queryClient.invalidateQueries({ queryKey: ['task'], exact: false })
    },
  })
}

/** Papelera (CLAUDE.md §6, fase 5): tareas borradas, global o de un proyecto. */
export function deletedTasksQueryKey(projectId: string | undefined) {
  return ['tasks', 'trash', projectId] as const
}

export function useDeletedTasksQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: deletedTasksQueryKey(projectId),
    queryFn: () => fetchAllTasks({ projectId, onlyDeleted: true }),
  })
}

export function useRestoreTaskMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/tasks/${id}/restore`, taskCardSchema, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false })
    },
  })
}

/**
 * Solo para la papelera: quién borró una tarea no está en `TaskCard`, así que
 * cada fila lo resuelve pidiendo el histórico y quedándose con el último
 * `deleted` (ver docs/DECISIONS.md, fase 5 — proyectos y etiquetas no tienen
 * este rastro, solo las tareas).
 */
export function useTaskActivityQuery(taskId: string) {
  return useQuery({
    queryKey: ['task-activity', taskId] as const,
    queryFn: () => apiRequest(`/tasks/${taskId}/activity`, z.array(activitySchema)),
    staleTime: 60_000,
  })
}

export function lastDeletedByActivity(activity: Activity[] | undefined): Activity | undefined {
  if (!activity) return undefined
  return [...activity].reverse().find((entry) => entry.type === 'deleted')
}

/** Búsqueda global (`/`, CLAUDE.md §6): título y descripción, veinte resultados como mucho. */
export function useSearchTasksQuery(q: string) {
  const trimmed = q.trim()
  return useQuery({
    queryKey: ['tasks', 'search', trimmed] as const,
    queryFn: () =>
      apiRequest(
        `/tasks${buildQueryString({ q: trimmed, limit: 20 })}`,
        pageSchema(taskCardSchema),
      ),
    enabled: trimmed.length > 0,
  })
}
